import { useState, useRef, useMemo, useCallback } from 'react';
import { unprojectToPlane, SCENE_VIEW } from '../engine/battlefield/projection';
import { measureBetween } from '../engine/battlefield/measure';
import { tokenEye } from '../engine/battlefield/tokens';
import { targetEnd } from '../engine/battlefield/relations';
import { pointerToScene } from '../utils/scenePointer';

/** A combatant terrain does not adjudicate measures against nothing. */
const EMPTY_SOLIDS = [];

/** Within this many metres of a token, the line snaps to them. */
const SNAP_METRES = 1.2;

/**
 * The nearest token to a ground point, if it is close enough.
 *
 * Snapping matters more than it sounds: a measurement to a spot beside a
 * man is not a measurement to the man, and the cover his barricade gives
 * him depends on his EYE height, not on the ground he is standing on.
 */
function snapToToken(point, tokens) {
  let best = null;
  let bestDistance = SNAP_METRES;
  for (const token of tokens) {
    const d = Math.hypot(token.x - point.x, token.z - point.z);
    if (d <= bestDistance) {
      bestDistance = d;
      best = token;
    }
  }
  return best;
}

/**
 * Where a pointer event puts one end of the tape: a marker's EYE, or bare
 * ground at a nominal eye height; null off the ground.
 *
 * THE MARKER UNDER THE POINTER FIRST, and the ground magnet only when
 * there is none. A marker's body is drawn above its feet, so from any
 * angle but straight down, the ground point under its body lies well
 * behind it - often further than the magnet's 1.2 m, which is why a tape
 * pulled from a man started from empty ground behind him unless you were
 * in the top view (v7.83.0). The browser's hit test knows exactly what
 * was pressed, at every pitch.
 */
function endAt(e, camera, tokens) {
  const { x, y } = pointerToScene(e);
  const ground = unprojectToPlane(x, y, 0, camera, SCENE_VIEW);
  if (!ground) return null;
  const pressed = e.target?.closest?.('[data-token-id]')?.getAttribute('data-token-id');
  const token = (pressed && tokens.find((t) => t.id === pressed)) || snapToToken(ground, tokens);
  return token ? { ...tokenEye(token), token, tokenId: token.id } : { x: ground.x, y: 1.6, z: ground.z, tokenId: null };
}

/**
 * An end with its marker looked up NOW. THE MARKER IS LOOKED UP, NEVER
 * REMEMBERED: an end holds an id, or a measurement keeps a snapshot of
 * somebody taken when the drag began - and then ticking "this one is
 * flying" changes nothing on screen.
 */
function liveEnd(end, tokens) {
  if (!end) return end;
  const token = end.tokenId ? tokens.find((t) => t.id === end.tokenId) : null;
  return token ? { ...end, token } : end;
}

/**
 * The live answer for the ends held. The TARGET end decides the cover: it
 * is their body a shot has to reach, and their height that says how much
 * of them is hidden.
 */
function lineFor(ends, tokens, { solids, weaponRange, isGm, relationRules }) {
  const from = liveEnd(ends.from, tokens);
  const to = liveEnd(ends.to, tokens);
  if (!to) return { from, to: from, measurement: null };
  const target = to.token;
  const ignores = from.token?.ignoresTerrain || target?.ignoresTerrain;
  return {
    from,
    to,
    measurement: measureBetween(from, to, ignores ? EMPTY_SOLIDS : solids, {
      weaponRange,
      includeHidden: isGm,
      // Cover counts only near the target (2026-10-25); a tape to bare ground has no target to be near.
      ...(target ? targetEnd(target, relationRules) : {}),
    }),
    ignoringTerrain: Boolean(ignores),
  };
}

/**
 * useBattlefieldMeasure - DRAG a line, read the range and what it crosses.
 *
 * A drag, because a drag is what a tape measure is. It briefly became two
 * clicks (v7.19.0) to get the camera out of its way, which worked and
 * cost more than it bought: clicking to start nudges the map, and nothing
 * about the gesture was guessable. The camera moved to the right and
 * middle buttons instead (v7.24.0), which frees the left button for the
 * work and leaves measuring the shape it always wanted to be.
 *
 * Both ends are EYE-height points, not ground positions: whether a shot
 * clears a barricade depends entirely on how tall the two combatants are
 * and what they are standing on. A free point uses a nominal eye height
 * so that measuring to bare ground still answers a sensible question.
 */
export default function useBattlefieldMeasure(params) {
  const { getCamera, tokens, solids, weaponRange, isGm, relationRules, onMeasuredTo } = params;
  /**
   * THE TWO ENDS, and nothing worked out from them.
   *
   * The measurement used to be computed once and stored, which froze it:
   * switching terrain off, or changing weapon, left last minute's answer
   * on screen until you measured again. Keeping only the endpoints and
   * deriving the rest - a memo over `solids` (the GM builds, or switches
   * terrain off) and `weaponRange` (a different gun) - means the readout
   * follows every condition it depends on, for free.
   */
  const [ends, setEnds] = useState(null);
  /** True while the tape is being pulled out - the crosshair cursor. */
  const [pulling, setPulling] = useState(false);
  const line = useMemo(
    () => (ends ? lineFor(ends, tokens, { solids, weaponRange, isGm, relationRules }) : null),
    [ends, tokens, solids, weaponRange, isGm, relationRules],
  );
  const { start, track, end, clear } = useTape({ getCamera, tokens, setEnds, setPulling, onMeasuredTo });
  return {
    line,
    clear,
    /** True while the tape is out: the cursor is a crosshair. */
    pending: pulling,
    measureHandlers: { onPointerDown: start, onPointerMove: track, onPointerUp: end, onPointerCancel: end },
  };
}

/** The pointer handlers that pull the tape out, follow it, let go and put it away. */
function useTape({ getCamera, tokens, setEnds, setPulling, onMeasuredTo }) {
  const drag = useRef(null);
  const pointAt = useCallback((e) => endAt(e, getCamera(), tokens), [getCamera, tokens]);
  /** Pull the tape out from here. */
  const start = useCallback(
    (e) => {
      const from = pointAt(e);
      if (!from) return;
      e.currentTarget.setPointerCapture?.(e.pointerId);
      drag.current = { id: e.pointerId, from };
      setPulling(true);
      setEnds({ from, to: null });
    },
    [pointAt, setPulling, setEnds],
  );
  const track = useTrack(drag, pointAt, setEnds);
  const end = useLetGo(drag, setPulling, onMeasuredTo);
  const clear = useCallback(() => {
    drag.current = null;
    setPulling(false);
    setEnds(null);
  }, [setPulling, setEnds]);
  return { start, track, end, clear };
}

/** Follow the pointer with the far end. */
function useTrack(drag, pointAt, setEnds) {
  return useCallback(
    (e) => {
      const d = drag.current;
      if (!d || d.id !== e.pointerId) return;
      const to = pointAt(e);
      if (!to) return;
      // Remembered so the release knows where the tape ACTUALLY ended.
      d.to = to;
      setEnds({ from: d.from, to });
    },
    [drag, pointAt, setEnds],
  );
}

/**
 * Let go. The line STAYS - a measurement you have to keep the button held
 * down to read is not a measurement, it is a peek.
 *
 * WHO THE TAPE LANDED ON, on release only. The magnet has always resolved
 * the far end to a combatant - it has to, because their EYE height is what
 * decides whether the barricade covers them - and simply never told
 * anyone. Reporting it mid-drag would flip the target panel at every
 * marker the tape swept past. Only when the tape TRAVELLED: a press and a
 * release in one place is not a measurement, it is a click - and a click
 * on a marker means something else entirely (see useTokenDrag).
 */
function useLetGo(drag, setPulling, onMeasuredTo) {
  return useCallback(
    (e) => {
      if (drag.current?.id !== e.pointerId) return;
      e.currentTarget.releasePointerCapture?.(e.pointerId);
      const finished = drag.current;
      drag.current = null;
      setPulling(false);
      if (finished?.to?.tokenId) onMeasuredTo?.(finished.to.tokenId);
    },
    [drag, setPulling, onMeasuredTo],
  );
}
